// Custo real de um item de nota de entrada — a conta que decide o `preco_custo` do produto.
//
// ⚠️ Ela existe aqui, e nao dentro da tela, porque a MESMA conta e' feita em tres lugares: a
// tela de importacao (previa, antes de gravar), a lista de Notas de Entrada (coluna "Custo
// Real") e agora a correcao de quantidade de um item ja importado. As duas primeiras nasceram
// separadas e ja divergiam — ver `custoRealDoItem` abaixo.
//
// O QUE ELA RESOLVE (21/09/2026): a nota 26111661 da MARTINS entrou com dois itens no fator
// errado — o PP 4X1,5 como **1 metro** onde eram 100 (custo do metro virou R$ 619,45) e o
// cordao 2x0,75 como **1.000** onde eram 100 (custo virou R$ 0,15). Corrigir a quantidade
// depois de importada exige refazer exatamente esta conta; se ela for reescrita no backend, o
// numero gravado deixa de bater com o que a tela mostrou, e ninguem percebe.
//
// A conta e' INVERSAMENTE proporcional a `qtdReal`: todo o numerador (valor da linha, rateio,
// IPI, imposto) independe da quantidade. E' por isso que corrigir o fator de 1 para 100 divide
// o custo por 100, exatamente.

/** O rateio de frete/despesas que a nota distribui entre os itens, proporcional ao valor. */
export function fatorRateioDaNota(args: {
  /** Total da nota + outras despesas informadas pelo operador. */
  totalNF: number;
  /** Soma dos produtos, como o XML traz. */
  somaXml: number;
  /** Soma do IPI de todos os itens (ele ja vem por item, nao se rateia). */
  totalIPI: number;
}): number {
  const compartilhados = args.totalNF - args.somaXml - args.totalIPI;
  return args.somaXml > 0 && compartilhados > 0 ? compartilhados / args.somaXml : 0;
}

/**
 * Custo de UMA unidade de estoque do item (o metro, nao o rolo).
 *
 * ⚠️ O imposto e' `difal` OU `st`, nunca a soma — e' a regra que a importacao usa pra GRAVAR, e
 * por isso e' ela que vale aqui. A lista de notas somava os dois; medido em 21/09/2026, os dois
 * nunca apareceram juntos (0 de 565 itens), entao as duas formas davam o mesmo numero e a
 * divergencia ficou escondida. Se algum dia um item trouxer ST e DIFAL ao mesmo tempo, a regra
 * precisa ser DECIDIDA (somar e' o que a leitura fiscal sugere) — e ai muda aqui, num lugar so.
 */
export function custoRealDoItem(args: {
  /** Valor total da linha, como o XML traz (preco da embalagem x quantidade). */
  valorTotal: number;
  /** IPI da linha. */
  valorIpi: number;
  /** DIFAL da linha. */
  valorDifal: number;
  /** ICMS-ST da linha. Ignorado quando ja vem embutido no valor dos produtos. */
  valorSt: number;
  /** Quantidade na unidade de ESTOQUE (`quantidade` x Qt.Emb). */
  qtdReal: number;
  /** Rateio da nota, de `fatorRateioDaNota`. */
  fatorRateio: number;
  /** `true` quando o ST ja esta somado no valor dos produtos e nao deve entrar de novo. */
  stEmbutido?: boolean;
  /** Preco unitario da nota — so usado quando a quantidade e' zero/invalida. */
  valorUnitario?: number;
}): number {
  if (!(args.qtdReal > 0)) return args.valorUnitario ?? 0;
  const st = args.stEmbutido ? 0 : args.valorSt;
  const imposto = args.valorDifal > 0 ? args.valorDifal : st;
  return (args.valorTotal * (1 + args.fatorRateio) + args.valorIpi + imposto) / args.qtdReal;
}

/**
 * O fator de embalagem que a quantidade corrigida implica — e' ele que fica guardado no vinculo
 * com o fornecedor e preenche o campo sozinho na PROXIMA importacao daquele codigo.
 *
 * ⚠️ Corrigir a quantidade sem corrigir isto faz o erro voltar ja preenchido na proxima nota,
 * e da segunda vez ninguem desconfia.
 */
export function fatorEmbalagemDe(qtdReal: number, quantidadeNota: number): number | null {
  if (!(quantidadeNota > 0) || !(qtdReal > 0)) return null;
  return qtdReal / quantidadeNota;
}
